import { useEffect, useState } from 'react';
import { PAYLINES, REEL_STRIP, slotPayout, type SlotSymbol, type SlotsState } from '../../engine/games/slots';
import { money } from '../../format';
import { Burst, Controls, secondChanceFrame, Stage, StatusLine, useAnimClock, useBurst, useLuckMoment, type TableProps } from './Frame';

type Grid = SlotSymbol[][]; // [reel][row]

/** When each reel stops, from the start of a spin (left to right). */
const REEL_STOPS = [520, 820, 1120];
const SPIN_MS = REEL_STOPS[2]!;
const LUCK_HOLD_MS = 1500;
const END_HOLD_MS = 300;
/** How fast symbols flick past on a turning reel. */
const FLICK_MS = 70;

const LABELS: Record<string, SlotSymbol> = { '7': 'SEV', BAR: 'BAR', BEL: 'BEL', CHR: 'CHR', LEM: 'LEM', '-': 'BLK' };

/** The losing spin a ★ second chance replaced, read from the engine's frame
 *  (its text grid, e.g. " BAR|BEL| - "). */
function parseGrid(frame: string): Grid | null {
  const rows = frame.split('\n').filter((l) => l.includes('|')).slice(0, 3).map((l) => l.split('|').map((c) => LABELS[c.trim()]));
  if (rows.length !== 3 || rows.some((r) => r.length !== 3 || r.some((c) => !c))) return null;
  return [0, 1, 2].map((reel) => [0, 1, 2].map((row) => rows[row]![reel]!));
}

export function SlotArt({ symbol }: { symbol: SlotSymbol }) {
  switch (symbol) {
    case 'SEV':
      return <span className="sym-seven">7</span>;
    case 'BAR':
      return <span className="sym-bar">BAR</span>;
    case 'BEL':
      return (
        <svg viewBox="0 0 48 48" className="sym-svg" aria-hidden="true">
          <path d="M24 6c-8 0-13 6-13 15v8l-4 6h34l-4-6v-8c0-9-5-15-13-15z" fill="#d9a93c" stroke="#7a5a14" strokeWidth="2" />
          <circle cx="24" cy="39" r="4" fill="#7a5a14" />
          <path d="M17 18c1-4 4-6 7-6" fill="none" stroke="#f3e2a6" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      );
    case 'CHR':
      return (
        <svg viewBox="0 0 48 48" className="sym-svg" aria-hidden="true">
          <path d="M16 30c4-10 8-17 16-22M32 30c-1-8-1-15 0-22" fill="none" stroke="#3f7a3a" strokeWidth="2.5" strokeLinecap="round" />
          <path d="M31 8c4 0 8 2 9 5-4 1-8 0-9-5z" fill="#4f8f45" />
          <circle cx="15" cy="34" r="8" fill="#b3302a" stroke="#6f1812" strokeWidth="1.5" />
          <circle cx="32" cy="35" r="8" fill="#c23a30" stroke="#6f1812" strokeWidth="1.5" />
          <circle cx="12" cy="31" r="2" fill="#f2b4a8" />
          <circle cx="29" cy="32" r="2" fill="#f2b4a8" />
        </svg>
      );
    case 'LEM':
      return (
        <svg viewBox="0 0 48 48" className="sym-svg" aria-hidden="true">
          <path d="M5 25c3-9 11-15 19-15s16 6 19 15c-3 9-11 14-19 14S8 34 5 25z" fill="#e8c93a" stroke="#8a7314" strokeWidth="1.5" />
          <path d="M14 21c3-4 7-6 11-6" fill="none" stroke="#fbeea0" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      );
    default:
      return <span className="sym-blank" aria-hidden="true" />;
  }
}

/** The five paylines drawn over the reels; winning ones light up. */
function Paylines({ lit }: { lit: Set<string> }) {
  const at = (reel: number, row: number) => `${50 + reel * 100},${50 + row * 100}`;
  return (
    <svg className="paylines" viewBox="0 0 300 300" preserveAspectRatio="none" aria-hidden="true">
      {PAYLINES.map((l) => (
        <polyline key={l.name} className={lit.has(l.name) ? "lit" : ""} points={l.rows.map((row, reel) => at(reel, row)).join(" ")} />
      ))}
    </svg>
  );
}

const SYMBOL_NAMES: Record<SlotSymbol, string> = { SEV: 'seven', BAR: 'bar', BEL: 'bell', CHR: 'cherry', LEM: 'lemon', BLK: 'blank' };

/** A pay row's reels: a symbol, or "any" (anything). */
type PayCell = SlotSymbol | 'any';
/** The pay glass, top prize first. Each row's multiple comes from the
 *  engine's own slotPayout on an example line, so it can't drift. */
const PAY_ROWS: { cells: PayCell[]; example: SlotSymbol[]; label: string; tag?: string }[] = [
  { cells: ['SEV', 'SEV', 'SEV'], example: ['SEV', 'SEV', 'SEV'], label: 'Three sevens' },
  { cells: ['BAR', 'BAR', 'BAR'], example: ['BAR', 'BAR', 'BAR'], label: 'Three BARs' },
  { cells: ['BEL', 'BEL', 'BEL'], example: ['BEL', 'BEL', 'BEL'], label: 'Three bells' },
  { cells: ['CHR', 'CHR', 'CHR'], example: ['CHR', 'CHR', 'CHR'], label: 'Three cherries' },
  { cells: ['LEM', 'LEM', 'LEM'], example: ['LEM', 'LEM', 'LEM'], label: 'Three lemons' },
  { cells: ['BEL', 'SEV', 'BAR'], example: ['SEV', 'BAR', 'BEL'], label: 'Any mix of sevens, BARs and bells', tag: 'any mix' },
  { cells: ['CHR', 'CHR', 'any'], example: ['CHR', 'CHR', 'BLK'], label: 'Two cherries' },
  { cells: ['CHR', 'any', 'any'], example: ['CHR', 'BLK', 'BLK'], label: 'One cherry' },
];

function PayCellArt({ cell }: { cell: PayCell }) {
  return cell === 'any' ? <span className="pay-any">any</span> : <SlotArt symbol={cell} />;
}

/**
 * The paytable as the machine's own lit glass (user, 2026-09-27: the text
 * sheet didn't fit the vibe): each winning line drawn with the reel
 * symbols, its multiple in the tray's LED digits, what it pays at your
 * current line bet, and the five paylines.
 */
function PayGlass({ perLine, onClose }: { perLine: number; onClose: () => void }) {
  return (
    <div className="sheet pay-glass" onClick={onClose} role="dialog" aria-label="Slots paytable">
      <div className="slot-sign pay-glass-sign">
        <span className="bulbs" aria-hidden="true" />
        <span className="slot-sign-text">Pays</span>
        <span className="bulbs" aria-hidden="true" />
      </div>
      <div className="pay-rows">
        {PAY_ROWS.map((r) => {
          const x = slotPayout(r.example).multiplier;
          return (
            <div key={r.label} className="pay-row" aria-label={`${r.label} pays ${x} times the line bet`}>
              <span className="pay-cells">
                {r.cells.map((c, i) => (
                  <span key={i} className="pay-cell">
                    <PayCellArt cell={c} />
                  </span>
                ))}
              </span>
              <span className="pay-tag">{r.tag}</span>
              <span className="pay-x">×{x}</span>
              <span className="pay-money">{perLine > 0 ? money(x * perLine) : ''}</span>
            </div>
          );
        })}
      </div>
      <div className="pay-foot">
        <svg className="pay-lines" viewBox="0 0 60 60" aria-hidden="true">
          {[0, 1, 2].map((r) => [0, 1, 2].map((c) => <rect key={`${r}${c}`} x={4 + c * 18} y={4 + r * 18} width="16" height="16" rx="2" />))}
          {PAYLINES.map((l) => (
            <polyline key={l.name} points={l.rows.map((row, reel) => `${12 + reel * 18},${12 + row * 18}`).join(' ')} />
          ))}
        </svg>
        <span>
          Five lines, three rows and two diagonals, each paying on its own. Each line bets a fifth of your bet{perLine > 0 ? ` (${money(perLine)})` : ''}.
        </span>
      </div>
      <span className="sheet-hint">Tap to close</span>
    </div>
  );
}

export function SlotsTable({ snapshot, actions }: TableProps) {
  const run = snapshot.run!;
  const state = run.tables.slots?.state as SlotsState | undefined;
  const anim = snapshot.anim;
  const luckFrame = secondChanceFrame(snapshot);
  const first = luckFrame ? parseGrid(luckFrame) : null;
  // A second chance plays the losing spin, holds on it, then spins again.
  const secondStart = first ? SPIN_MS + LUCK_HOLD_MS : 0;
  const elapsed = useAnimClock(snapshot, actions, secondStart + SPIN_MS + END_HOLD_MS);
  const [burst, fading, setBurst] = useBurst();
  const [showPay, setShowPay] = useState(false);
  // A spin (the Spin button or the lever) closes the pay glass (user,
  // 2026-09-27), so the reels are in view as they turn.
  useEffect(() => {
    if (anim) setShowPay(false);
  }, [anim]);

  // The losing spin stops first; then the second chance, then the re-spin.
  useLuckMoment(snapshot, elapsed, first ? SPIN_MS + 350 : null, () => ({ what: 'No win, so the reels spin again.', saved: !!state?.lucky }), setBurst);

  const grid = state?.grid ?? null;
  const n = REEL_STRIP.length;
  const cell = (reel: number, row: number): { symbol: SlotSymbol; spinning: boolean } => {
    if (anim && grid) {
      const inFirst = !!first && elapsed < secondStart;
      const target = inFirst ? first! : grid;
      const t = inFirst ? elapsed : elapsed - secondStart;
      if (t < REEL_STOPS[reel]!) return { symbol: REEL_STRIP[(Math.floor(t / FLICK_MS) + reel * 7 + row) % n]!, spinning: true };
      return { symbol: target[reel]![row]!, spinning: false };
    }
    if (grid) return { symbol: grid[reel]![row]!, spinning: false };
    return { symbol: REEL_STRIP[(reel * 11 + row) % n]!, spinning: false };
  };

  // Winning lines light up once the reels have stopped.
  const winning = new Set<string>();
  if (state?.grid && !anim) {
    for (const w of state.wins) {
      const line = PAYLINES.find((l) => l.name === w.name);
      line?.rows.forEach((row, reel) => winning.add(`${reel}-${row}`));
    }
  }
  const winningLines = new Set(state?.grid && !anim ? state.wins.map((w) => w.name) : []);
  const net = (state?.lastReturn ?? 0) - (state?.lastBet ?? 0);
  const result = state?.grid
    ? state.lastReturn > 0
      ? `${state.lucky ? '★ ' : ''}Pays ${money(state.lastReturn)} (${net >= 0 ? '+' : '−'}${money(Math.abs(net))})`
      : `No win (−${money(state.lastBet)})`
    : undefined;
  const lineNames = state?.wins.length ? state.wins.map((w) => `${w.name.replace('D\\', 'Diag ↘').replace('D/', 'Diag ↗')} ×${w.multiplier}`).join(' · ') : null;

  const spinning = !!anim && elapsed < secondStart + SPIN_MS;
  // The lever: pulled at the start of each spin (and the re-spin), then springs back.
  const pulled = !!anim && (elapsed < 350 || (!!first && elapsed >= secondStart && elapsed < secondStart + 350));
  const perLine = (run.tables.slots?.bet ?? 0) / 5;
  const paid = state?.grid && !anim ? state.lastReturn : 0;
  const pull = (e: { stopPropagation(): void }) => {
    e.stopPropagation();
    if (!anim) actions.tableOption("spin");
  };

  return (
    <>
      <Stage onClick={anim ? actions.skip : undefined} className="slots-stage">
        <Burst burst={burst} fading={fading} pending={!!anim && elapsed < secondStart + SPIN_MS} />
        <div className={`slot-cabinet${spinning ? " spinning" : ""}${paid > 0 ? " paying" : ""}`}>
          <div className="slot-sign">
            <span className="bulbs" aria-hidden="true" />
            <span className="slot-sign-text">Lucky 7s</span>
            <span className="bulbs" aria-hidden="true" />
          </div>
          <div className="slot-window">
            <div className="reels" role="img" aria-label={grid && !anim ? `Reels: ${[0, 1, 2].map((row) => [0, 1, 2].map((r) => SYMBOL_NAMES[grid[r]![row]!]).join(", ")).join("; ")}` : "Reels"}>
              {[0, 1, 2].map((reel) => (
                <div key={reel} className="reel">
                  {[0, 1, 2].map((row) => {
                    const c = cell(reel, row);
                    return (
                      <div key={row} className={`reel-cell${c.spinning ? " spinning" : ""}${winning.has(`${reel}-${row}`) ? " win" : ""}${!grid ? " idle" : ""}`}>
                        <SlotArt symbol={c.symbol} />
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            <Paylines lit={winningLines} />
            <div className="slot-glass" aria-hidden="true" />
          </div>
          <div className="slot-tray">
            <span>5 LINES × <b>{money(perLine)}</b></span>
            <span>PAID <b className={paid > 0 ? "lit" : ""}>{money(paid)}</b></span>
          </div>
          <button className={`slot-lever${pulled ? " pulled" : ""}`} onClick={pull} disabled={!!anim} aria-label="Pull the lever to spin">
            <span className="lever-arm" />
            <span className="lever-knob" />
          </button>
        </div>
        {showPay && <PayGlass perLine={perLine} onClose={() => setShowPay(false)} />}
        <StatusLine snapshot={snapshot} result={result && (lineNames ? <><div>{result}</div><div className="result-sub">{lineNames}</div></> : result)} tone={net > 0 ? 'win' : net < 0 ? 'loss' : ''} />
      </Stage>
      <Controls
        snapshot={snapshot}
        actions={actions}
        hide={['paytable']}
        extra={[{ id: 'pay', label: showPay ? 'Close' : 'Pays', onClick: () => setShowPay((s) => !s) }]}
        onAct={() => setBurst(null)}
      />
    </>
  );
}
